import { DateInput, MoneyInput } from "@/components/shared";
import { Button } from "@/components/ui/button";
import Currency from "@/components/financial/Currency";
import type { InstallmentDraft } from "../hooks/generateInstallments";
import { sumInstallmentAmounts } from "../hooks/generateInstallments";

interface CreateContractStepTwoProps {
  installments: InstallmentDraft[];
  totalValue: number;
  loading?: boolean;
  errorMessage?: string;
  onChangeInstallment: (
    index: number,
    field: "amount" | "dueDate",
    value: string
  ) => void;
  onBack: () => void;
  onSubmit: () => void;
}

export default function CreateContractStepTwo({
  installments,
  totalValue,
  loading,
  errorMessage,
  onChangeInstallment,
  onBack,
  onSubmit,
}: CreateContractStepTwoProps) {
  const sum = sumInstallmentAmounts(installments);
  const mismatch = Math.abs(sum - totalValue) > 0.01;

  return (
    <div className="space-y-6">
      {mismatch && (
        <div
          className="border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300"
          role="alert"
        >
          A soma das parcelas ({sum.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })})
          não corresponde ao valor total do contrato.
        </div>
      )}

      {errorMessage && (
        <div
          className="border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
        {installments.map((installment, index) => (
          <div
            key={installment.number}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-primary-100 dark:border-primary-800 pb-4"
          >
            <div className="flex items-end">
              <span className="text-sm text-primary-600 dark:text-primary-400 pb-2">
                Parcela #{installment.number.toString().padStart(2, "0")}
              </span>
            </div>
            <MoneyInput
              label="Valor"
              value={installment.amount.toFixed(2)}
              onChange={(e) => onChangeInstallment(index, "amount", e.target.value)}
              loading={loading}
              variant="primary"
            />
            <DateInput
              label="Vencimento"
              value={installment.dueDate}
              onChange={(e) =>
                onChangeInstallment(index, "dueDate", e.target.value)
              }
              loading={loading}
              variant="primary"
            />
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center pt-2 border-t border-primary-200 dark:border-primary-800">
        <span className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
          Total das parcelas
        </span>
        <span className={`text-lg font-light ${mismatch ? "text-red-600" : "text-primary-950 dark:text-white"}`}>
          <Currency value={sum} />
        </span>
      </div>

      <div className="flex flex-wrap gap-4">
        <Button type="button" variant="secondary" onClick={onBack} disabled={loading}>
          VOLTAR
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={loading || mismatch}
        >
          {loading ? "SALVANDO..." : "SALVAR CONTRATO"}
        </Button>
      </div>
    </div>
  );
}
